import { z } from 'zod'

import { dateSchema } from '~/shared/lib/schemas'
import { ApiModel } from './core'

const attributeSchema = z.object({
  extId: z.string(),
  plateNumber: z.string().nullable(),
  vin: z.string().nullable(),
  sts: z.string().nullable(),
  brandTitle: z.string().nullable(),
  modelTitle: z.string().nullable(),
  subdivisionExtId: z.string().nullable(),
  subdivisionName: z.string().nullable(),
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)

export type CarAttributes = z.infer<typeof attributeSchema>

/*
 * Данные автомобиля, которыми заполняются поля штрафа при ручном создании.
 * */
export type CarFormOption = {
  id: UniqueId
  label: string
  carExtId: string
  carVin: string
  carPlateNumber: string
  carSts: string
  carBrandTitle: string
  carModelTitle: string
  tmsSubdivision: string
  tmsSubdivisionName: string
}

export class Car extends ApiModel<typeof schema, CarAttributes> {
  static jsonApiType = 'cars'

  static schema = schema

  getPlateNumber(): string {
    return this.getAttribute('plateNumber') ?? ''
  }

  getVin(): string {
    return this.getAttribute('vin') ?? ''
  }

  getBrandModel(): string {
    const brand = this.getAttribute('brandTitle')
    const model = this.getAttribute('modelTitle')

    return [brand, model].filter(Boolean).join(' ')
  }

  getLabel(): string {
    return [this.getPlateNumber(), this.getBrandModel(), this.getVin()]
      .filter(Boolean)
      .join(' · ')
  }

  getFormOption(): CarFormOption {
    return {
      id: this.getApiId() as UniqueId,
      label: this.getLabel(),
      carExtId: this.getAttribute('extId'),
      carVin: this.getVin(),
      carPlateNumber: this.getPlateNumber(),
      carSts: this.getAttribute('sts') ?? '',
      carBrandTitle: this.getAttribute('brandTitle') ?? '',
      carModelTitle: this.getAttribute('modelTitle') ?? '',
      tmsSubdivision: this.getAttribute('subdivisionExtId') ?? '',
      tmsSubdivisionName: this.getAttribute('subdivisionName') ?? '',
    }
  }

  /*
   * Автомобиль ищется и по госномеру, и по VIN: на постановлении может быть указан
   * только один из них.
   * */
  static async fetchOptions(search: string): Promise<CarFormOption[]> {
    const byPlateNumber = await Car.where('plateNumber', search).get(1)
    const options = byPlateNumber.getData().map((car) => car.getFormOption())

    if (options.length > 0) return options

    const byVin = await Car.where('vin', search).get(1)
    return byVin.getData().map((car) => car.getFormOption())
  }
}
